import {useCallback, useEffect, useMemo, useState} from 'react';

import {useMatch, useLocation} from 'react-router-dom';

import {getRoute} from 'config/navigation';
import {StaticConfig} from 'config/staticConfig';
import {ChatBotEventName, ChatBotProactiveEventNames} from 'features/ChatBot/enums';
import {getChatBotTransciptUrl, getChatBotUrl, getChatBotDomainUrl} from 'utils/links/appLinks';
import {loadScript} from 'utils/loadScript';

declare let Genesys: any;

const initialWidgetState = {
  isOpen: false,
  hasOpened: false,
  loadingWidget: false,
  scriptLoaded: false
};

const registerAuthenticationProviderEvents = new Event('registerAuthenticationProviderEvents');
const updateChatMessagingServiceEvent = new Event('updateChatMessagingServiceEvent');

const loadChatBot = () => {
  const chatConfig = [];
  if (StaticConfig.chatBotDeploymentID !== '') {
    chatConfig.push(loadScript(getChatBotUrl(), undefined, true));
  }
  if (StaticConfig.chatBotTranscriptID !== '') {
    //@ts-ignore
    window.toolbar_publicId = StaticConfig.chatBotTranscriptID;
    //@ts-ignore
    window.MESSENGER_DOMAIN = getChatBotDomainUrl();

    chatConfig.push(loadScript(getChatBotTransciptUrl(), undefined));
  }
  return chatConfig && Promise.all(chatConfig);
};

const openChatBot = () => {
  Genesys('command', 'Messenger.open');
};

export const useChatBot = () => {
  const [{isOpen, hasOpened, loadingWidget, scriptLoaded}, setWidgetState] = useState(initialWidgetState);
  const proactiveOffer = useProactiveChat(scriptLoaded);
  const loadScriptForProactiveEvent = useTriggerChatBotEvent(scriptLoaded);
  useSetDatabaseGenesysQuoting(scriptLoaded);
  useGenesysAuthenticationEvents(scriptLoaded);

  const updateWidgetState = useCallback((fieldsToUpdate: Partial<typeof initialWidgetState>) => {
    setWidgetState((prev) => ({...prev, ...fieldsToUpdate}));
  }, []);

  const persistChat = useMemo(() => window.sessionStorage.getItem('ava_button') !== null, []);
  const preLoadForProactiveChat = !!useMatch(getRoute('/claims/fnol/review'));

  const loadChatAndSubscribeEvents = useCallback(
    (openOnLoad?: boolean) => {
      loadChatBot()
        .then(() => {
          updateWidgetState({scriptLoaded: true});
          Genesys('subscribe', 'Messenger.opened', function () {
            updateWidgetState({isOpen: true, hasOpened: true, loadingWidget: false});
          });
          Genesys('subscribe', 'Messenger.closed', function () {
            updateWidgetState({isOpen: false});
          });
          Genesys('subscribe', 'MessagingService.messagesReceived', function () {
            window.dispatchEvent(updateChatMessagingServiceEvent);
          });
          Genesys('subscribe', 'MessagingService.sendingMessage', function () {
            window.dispatchEvent(updateChatMessagingServiceEvent);
          });
          if (openOnLoad) {
            Genesys('subscribe', 'Messenger.ready', openChatBot);
          }
        })
        .catch(() => {
          updateWidgetState({loadingWidget: false});
        });
    },
    [updateWidgetState]
  );

  useEffect(() => {
    if (!scriptLoaded && (persistChat || preLoadForProactiveChat || loadScriptForProactiveEvent)) {
      loadChatAndSubscribeEvents();
    }
  }, [scriptLoaded, persistChat, preLoadForProactiveChat, loadScriptForProactiveEvent, loadChatAndSubscribeEvents]);

  const onClick = useCallback(() => {
    updateWidgetState({loadingWidget: true});
    if (!scriptLoaded) {
      loadChatAndSubscribeEvents(true);
      return;
    }
    hasOpened ? openChatBot() : Genesys('subscribe', 'Messenger.ready', openChatBot);
  }, [updateWidgetState, scriptLoaded, hasOpened, loadChatAndSubscribeEvents]);

  return {widgetState: {isOpen, loadingWidget, proactiveOffer}, onClick};
};

const useGenesysAuthenticationEvents = (scriptLoaded: boolean) => {
  useEffect(() => {
    if (scriptLoaded && StaticConfig.oktaClientIdAuthMessenger) {
      window.dispatchEvent(registerAuthenticationProviderEvents);
    }
  }, [scriptLoaded]);
};

const useProactiveChat = (isScriptLoaded: boolean) => {
  const [proactiveOffer, setProactiveOffer] = useState(false);

  useEffect(() => {
    if (isScriptLoaded) {
      Genesys('subscribe', 'Engage.inviteOffered', ({data}: any) => {
        setProactiveOffer(true);
      });

      Genesys('subscribe', 'MessagingService.conversationDisconnected', function () {
        Genesys('command', 'Database.remove', {name: 'messaging.customAttributes'});
      });
    }
  }, [isScriptLoaded]);

  useEffect(() => {
    if (proactiveOffer) {
      const onAcknowledgedOrDissmised = (data?: any) => {
        setProactiveOffer(false);
        const actionMapId = data?.data?.journeyContext?.triggeringAction?.actionMap?.id;
        if (actionMapId) {
          Genesys('command', 'Database.update', {
            messaging: {customAttributes: {rejectedActionMapId: actionMapId}}
          });
        }
      };

      Genesys('subscribe', 'Engage.inviteAccepted', () => {
        onAcknowledgedOrDissmised();
        Genesys('command', 'Database.set', {messaging: {customAttributes: {proactiveChat: 'true'}}});
      });
      Genesys('subscribe', 'Engage.inviteRejected', onAcknowledgedOrDissmised);
      Genesys('subscribe', 'Engage.inviteIgnored', onAcknowledgedOrDissmised);
    }
  }, [proactiveOffer]);

  return proactiveOffer;
};

const useTriggerChatBotEvent = (isScriptLoaded: boolean): boolean => {
  const [eventName, setEventName] = useState<ChatBotEventName>();

  useEffect(() => {
    document.addEventListener(triggerChatBotEventName, (data: TriggerChatBotEvent) => {
      setEventName(data.detail.eventName);
    });
  }, [setEventName]);

  useEffect(() => {
    if (isScriptLoaded && eventName) {
      Genesys('subscribe', 'Journey.ready', () => {
        Genesys('command', 'Journey.record', {eventName});
        setEventName(undefined);
      });
    }
  }, [isScriptLoaded, eventName]);

  return (
    !!eventName &&
    Object.values(ChatBotProactiveEventNames).some((proactiveEventName) => proactiveEventName === eventName)
  );
};

const triggerChatBotEventName = 'triggerChatBotEvent';
class TriggerChatBotEvent extends CustomEvent<{eventName: ChatBotEventName}> {}

export const triggerChatBotEvent = (eventName: ChatBotEventName) => {
  const chatBotInviteEvent = new TriggerChatBotEvent(triggerChatBotEventName, {
    detail: {
      eventName
    }
  });

  document.dispatchEvent(chatBotInviteEvent);
};

const useSetDatabaseGenesysQuoting = (scriptLoaded: boolean) => {
  const location = useLocation();

  useEffect(() => {
    if (scriptLoaded && window.location.pathname.includes(getRoute('/quoting'))) {
      Genesys('subscribe', 'Database.ready', function () {
        Genesys('command', 'Database.update', {
          messaging: {customAttributes: {pageURL: `${StaticConfig.baseAemApi}${window.location.pathname}`}}
        });
      });
    }
  }, [scriptLoaded, location]);
};
